import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import APIData, {
  setDefaultAPIData,
} from "../../../../interfaces/apiData";
import { Button, Card, Table, Pagination, Input, Menu, Select, Navbar, Dropdown } from "react-daisyui";
import { LoaderData } from "../../../../types/LoaderData";
import debounce from "lodash.debounce";
import PaginationBar from "../../../../components/PaginationBar";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

import ParticipantTable from "./ParticipantTable";
import { escapeRegExp } from "../../../../utils/escapeReg";

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ExportConfirmationModal from "../ExportConfirmationModal";
import ModifyResponsesModal from "../ModifyResponsesModal";
import ActivateParticipantConfirmModal from "../ActivateParticipantConfirmModal";
import ProcessingModal from "../ProcessingModal";
import ActionConfirmationModal from "../ActionConfirmationModal";
import ViewReportModal from "../ViewReportModal";
import CohortReportConfirmationModal from "../CohortReportConfirmationModal";
import ExportResponsesSPSSModal from "../ExportResponsesSPSS";
import ExportResponsesCSVModal from "../ExportResponsesCSVModal";
import { axiosFileDownload } from "../../../../utils/axiosFileDownload";

function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const ViewParticipants = ({ studyId, studyTitle, study, hasCohortReport, hasIndividalReport }: any) => {
  let revalidator = useRevalidator();
  const navigate = useNavigate();

  const [participants, setParticipants] = useState<APIData>(
    setDefaultAPIData()
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<string>("10");
  const [orderBy, setOrderBy] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [surname, setSurname] = useState<string>("");
  const [apiParams, setApiParams] = useState<string>("");
  const [participantId, setParticipantId] = useState<number>(0);
  const [selectedRow, setSelectedRow] = useState<Set<number>>(new Set());
  const [isAllRowSelected, setIsAllRowSelected] = useState<boolean>(false);
  const [selectedTokens, setSelectedTokens] = useState<Set<string>>(new Set());

  const [isModifyResponsesModalOpen, setIsModifyResponsesModalOpen] = useState(false);
  
  const openModifyResponsesModal = () => setIsModifyResponsesModalOpen(true);
  const closeModifyResponsesModal = () => setIsModifyResponsesModalOpen(false);
  const getParticipantId = (ids: number) => setParticipantId(ids);

  const [isParticipantsActivated, setIsParticipantsActivated] = useState<boolean>(false);
  //const [emailedReport, errorEmailedReport] = useEmailedReportStatus(studyId, participants);

  const fetchParticipants = async () => {
    const params = {
      study: studyId,
      page_size: pageSize,
      page: page,
      order_by: orderBy,
    };
    const result: APIData = await (
      await axios.get(`/api/v1/participants/`, { params })
    ).data;
    setParticipants(result);
  };

  const [emailedReport, setEmailedReport] = useState<boolean>(false);
  const [emailedReportStatus, setEmailedReportStatus] = useState<string>('');
  // const [errorEmailedReport, setErrorEmailedReport] = useState<Error | null>(null);
  const intervalIdRef = useRef<any>(null);

  const fetchReportStatus = async () => {
    try {
        
        const response = await axios.get(`/api/v1/study/${studyId}/get_email_task_status/`);
        setEmailedReportStatus(response.data.status)
        console.log(`email report: ${response.data.status}, ${response.data.individual_report_ids}`)
        if(response.data.status === 'SUCCESS' && intervalIdRef.current){
          console.log(`email report success: ${response.data.status}, ${response.data.sending_individual_report}`)
          setEmailedReportStatus(response.data.status)
          fetchParticipants(); //update participants after email is successful
          clearInterval(intervalIdRef.current);
        }
    } catch (error) {
        clearInterval(intervalIdRef.current);
        // setErrorEmailedReport(error);
        console.error("Error fetching the report status:", error);
    }
  };


  // To be fix - get stuck in infinite loop
  useEffect(() => {
    // Fetch the report status immediately when the component mounts
    fetchReportStatus();

    // Set up an interval to poll the status
    intervalIdRef.current = setInterval(fetchReportStatus, 1000);

    // Clear the interval when the component is unmounted
    return () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
        }
    };
  }, [emailedReportStatus]);

  useEffect(() => {
    fetchParticipants();
  }, [page, pageSize, filter, orderBy, studyId]);

  useEffect(() => {
    /** Reset all checked boxes when filters are applied */
    setSelectedRow(new Set());
  }, [filter]);

  /** Checkboxes functions */
  const toggleSelection = (id: number, token: string) => {
    console.log('toggleSelection 01', id, token)
    const newSelectedRow = new Set(selectedRow);
    const newSelectedTokens = new Set(selectedTokens);
   
    if (selectedRow.has(id)) {
      newSelectedRow.delete(id);
      newSelectedTokens.delete(token);
      console.log('toggleSelection delete', newSelectedTokens)
    } else {
      newSelectedRow.add(id);
      newSelectedTokens.add(token);
      console.log('toggleSelection add', newSelectedTokens)
    }
    setSelectedRow(newSelectedRow);
    setSelectedTokens(newSelectedTokens);
    console.log('toggleSelection', newSelectedRow, newSelectedRow.size )
    //console.log('toggleSelection', newSelectedTokens, newSelectedTokens.size )
  };

  const selectAllRow = () => {
    const newSelectedRow = new Set(selectedRow);
    const newSelectedTokens = new Set(selectedTokens);

    if (isAllRowSelected) {
      // if all rows are selected, deselect all ON CURRENT PAGE
      for (const participant of participants?.results) {
        newSelectedRow.delete(participant.id);
        newSelectedTokens.delete(participant.token);
      }
    } else {
      // if not all rows are selected, select all ON CURRENT PAGE
      for (const participant of participants?.results) {
        newSelectedRow.add(participant.id);
        newSelectedTokens.add(participant.token);
      }
    }
    setSelectedRow(newSelectedRow);
    setSelectedTokens(newSelectedTokens);
  };

  useEffect(() => {
    // Determine if all studies on the current page are selected
    const allRowSelected = participants?.results.every((participant) =>
      selectedRow.has(participant.id)
    );
    setIsAllRowSelected(allRowSelected);
  }, [participants, selectedRow]);

  /** End of Checkboxes functions */

  // Change the current page of the paginated data
  function changePage(pageNum: number) {
    //navigate(`/studies?page=${number}`)
    setPage(pageNum);
    fetchParticipants();
  }

  function changePageSize(size: string) {
    const totalPages = Math.ceil(participants?.total / parseInt(size));

    // If the current page is greater than the total pages calculated by the new page size,
    // reset the page to the last available page.
    let newPage = page > totalPages ? totalPages : page;
    setPage(newPage);
    setPageSize(size);
    fetchParticipants();
  }

  /** Search title functions */
  // Create debounced version of the search
  useEffect(() => {
    const searchTitle = debounce(async () => {
      if (filter) {
        const searchTerm = filter.toLowerCase();
        setFilter(searchTerm);
        //console.log('escapedFilter ', escapedFilter)
        setParticipants((prevState) => ({
          ...prevState,
          results: prevState.results.filter((participant) =>
            participant.CFirname.toLowerCase().includes(searchTerm)
          ),
        }));

        // setParticipants(participants => ({
        //   ...participants,
        //   results: participants.results.filter(participant => participant.CFirname.toLowerCase().includes(searchTerm))
        // }));
        console.log("participants filter", participants);
      }
    }, 100); // 1000ms delay

    searchTitle();

    // Cleanup
    return () => {
      searchTitle.cancel();
    };
  }, [filter]);

  function changeFilter(newFilter: string) {
    setFilter(newFilter);
    //navigateWithParams({ filter: newFilter });
  }
  /**End of Search title functions */

  function changeOrderBy(newOrderBy: string){
    setOrderBy(newOrderBy);
  }

  // Reset all parameters to their defaults
  // function resetParams() {
  //   setPageSize("10");
  //   setFilter("");
  //   fetchParticipants();
  //   setSelectedRow(new Set());
  // }

  /* Export reports funtions */
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportAllModalOpen, setIsExportAllModalOpen] = useState(false);
  const [isCohortExportModalOpen, setIsCohortExportModalOpen] = useState(false);

  const ErrorToast = (error: any) => {
    const errTitle = error.message;
          const errBody: { [key: string]: string[] } = error.response.data;

          toast.error(
            <div className="min-w-[380px]">
              <p>Backend error: {errTitle}</p>
              <div>
              <ul className="mt-2">
                {Object.entries(errBody).map(([key, messages]) => (
                  <li key={key}>
                    <strong>{key}:</strong>
                    <ul>
                      {messages.map((message, index) => (
                        <li key={index}>{message}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              </div>
            </div>, 
            { autoClose: 30000 }
          );
  }

  const exportModalContent = () => {
    return(
      <>
       <div>Are you sure you want to export the selected studies?</div>
        {selectedRow.size > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            You have selected {selectedRow.size} studies:
            {Array.from(selectedRow).join(', ')}.
          </p>
        )}
      </>
    )
  }

  const deleteResponsesContent = () => {
    return (
      <div className='flex flex-col gap-2'>
        <h1 className='text-red-500'>Caution</h1>
        <h3><span className='text-accent'>Study Title: </span> {studyTitle}</h3>
        <h3><span className='text-accent'>Partcipant ID: </span>{participantId}</h3>
        <p>By confirming this action, you will initiate the process to delete the participant's responses in LimeSurvey permanently.</p><p> This will also allow the participant to retake the survey.</p>
        Click "Confirm" to proceed and remove the participant's responses. <br />
      </div>
    )
  }

  /* Participant activation functions */
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isActivateParticipantsModalOpen, setIsActivateParticipantsModalOpen] = useState(false);
  const [isActivateSelectedModalOpen, setIsActivateSelectedModalOpen] = useState(false);
  const [isEmailSelectedModalOpen, setIsEmailSelectedModalOpen] = useState(false);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const [isViewReportModalOpen, setIsViewReportModalOpen] = useState(false);

  const openActivateModal = () => setIsActivateModalOpen(true);
  const openEmailModal = () => setIsEmailModalOpen(true);
  const openActivateParticipantsModal = () => setIsActivateParticipantsModalOpen(true);
  const openActivateSelectedModal = () => setIsActivateSelectedModalOpen(true);
  const openEmailSelectedModal = () => setIsEmailSelectedModalOpen(true);
  const openExportCohortModal = () => setIsCohortExportModalOpen(true);
  const openViewReportModal = () => setIsViewReportModalOpen(true);

  const closeActivateModal = () => setIsActivateModalOpen(false);
  const closeEmailModal = () => setIsEmailModalOpen(false);
  const closeActivateParticipantsModal = () => setIsActivateParticipantsModalOpen(false);
  const closeActivateSelectedModal = () => setIsActivateSelectedModalOpen(false);
  const closeEmailSelectedModal = () => setIsEmailSelectedModalOpen(false);
  const closeExportCohortModal = () => setIsCohortExportModalOpen(false);
  const closeViewReportModal = () => setIsViewReportModalOpen(false);

  /* Participant CSV download */
  const [isExportParticipantCSVOpen, setIsExportParticipantCSVOpen] = useState(false);
  /* Study responses CSV download */
  const [isExportStudyResponsesCSVOpen, setIsExportStudyResponsesCSVOpen] = useState(false);
  /* Study responses SPSS download */
  const [isExportStudyResponsesSPSSOpen, setIsExportStudyResponsesSPSSOpen] = useState(false);

  const [isEmailReportAllOpen, setIsEmailReportAllOpen] = useState(false);

  const [isEmailReportSelectiveOpen, setIsEmailReportSelectiveOpen] = useState(false);

  const [isForceCalculationsOpen, setIsForceCalculationsOpen] = useState(false);


  const handleSuccessActivation = () => setIsParticipantsActivated(true)

  const activateSelectedRow = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/activate_participants/`, { ids: Array.from(selectedRow) })
      revalidator.revalidate();
      fetchParticipants();
      toast.success("Selected Participants Activated!");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsActivateSelectedModalOpen(false);
    }
  }

  const activateParticipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/activate_all_participants/`)
      revalidator.revalidate();
      fetchParticipants();
      toast.success("All Participants Activated!");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsActivateParticipantsModalOpen(false);
    }
  }

  const emailParticipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/email_all_participants/`)
      revalidator.revalidate();
      fetchParticipants();
      toast.success("All Participants Emailed!");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsEmailModalOpen(false)
    }
  }

  const emailSelectedParticipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/email_participants/`, { ids: Array.from(selectedRow) })
      revalidator.revalidate();
      fetchParticipants();
      toast.success("Selected Participants Emailed!");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsEmailSelectedModalOpen(false)
    }
  }

  const exportSelectedRow = () => {
    return axios.post(`/api/v1/study/${studyId}/export_individual_report/`, { study_id: studyId, ids: Array.from(selectedRow) }, { responseType: 'blob' })
      .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${studyTitle}_id_${studyId}_individual_reports.zip`); // or any other extension
          document.body.appendChild(link);
          link.click();
      })
      .catch(async (error) => {
        if (error.response?.data) {
            try {
                // Convert blob to JSON to get error message
                const errorText = await error.response.data.text();
                const errorJson = JSON.parse(errorText);
                console.error("API Error:", errorJson);
                toast.error(errorJson.error || "An unknown error occurred.");
            } catch (e) {
                console.error("Failed to parse error response:", e);
                toast.error("An unknown error occurred.");
            }
        } else {
            console.error("Request failed:", error);
            toast.error("Failed to generate report.");
        }
      })
      .finally(() => setIsExportModalOpen(false));
  };

  const exportAllRow = () => {
    return axios.post(`/api/v1/study/${studyId}/export_all_individual_report/`)
      .then(response => {
        const taskId = response.data.task_id;
        if (!taskId) {
          throw new Error('Task ID not returned by the server');
        }
        return taskId;
      })
      .catch(e => {
        toast.error(`Error creating individial reports - ${e?.response.data}`)
      })
  };

  const downloadParticipantReport = () => {
    return axios.post(`/api/v1/study/${studyId}/export_individual_report/`, { study_id: studyId, ids: [participantId] }, { responseType: 'blob' })
      .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${studyTitle}_id_${studyId}_Participant_${participantId}_individual_reports.zip`); // or any other extension
          document.body.appendChild(link);
          link.click();
      })
      .catch(async (error) => {
        if (error.response?.data) {
            try {
                // Convert blob to JSON to get error message
                const errorText = await error.response.data.text();
                const errorJson = JSON.parse(errorText);
                console.error("API Error:", errorJson);
                toast.error(errorJson.error || "An unknown error occurred.");
            } catch (e) {
                console.error("Failed to parse error response:", e);
                toast.error("An unknown error occurred.");
            }
        } else {
            console.error("Request failed:", error);
            toast.error("Failed to generate report.");
        }
      })
  };

  const exportCohortExport = (data?: any) => {
    return axios
      .post(`/api/v1/study/${studyId}/cohort_report/`, data)
      .then(response => {
        const taskId = response.data.task_id;
        if (!taskId) {
          throw new Error('Task ID not returned by the server');
        }
        return taskId;
      })
      .catch(e => {
        toast.error(`Error creating cohort reports - ${e?.response.data}`)
        setIsCohortExportModalOpen(false)
      })  };

  const startPolling = (
    taskId: string, 
    reportType: "individual" | "cohort",
    onDownload: (taskId: string, reportType: "individual" | "cohort", onSuccessMessage: string, onFailureMessage: string) => void, 
    onSuccessMessage = 'Report generation complete! Downloading...', 
    onFailureMessage = 'Report generation failed. Check logs for more details.', 
    closeModal: () => void
  ) => {
    const intervalId = setInterval(async () => {
      const taskStatus = await pollTaskStatus(taskId); // Function to poll the task status
      if (taskStatus === 'SUCCESS') {
        clearInterval(intervalId);
        onDownload(taskId, reportType, onSuccessMessage, onFailureMessage);
        closeModal(); // Handle modal close logic
      } else if (taskStatus === 'FAILURE') {
        clearInterval(intervalId);
        toast.error(onFailureMessage);
        closeModal()
      }
    }, 5000); // Poll every 5 seconds
  };

  const pollTaskStatus = async (taskId: string) => {
    try {
      const response = await axios.get(`/api/v1/task/check_task_status/`, {
        params: { id: taskId },
      });
      return response.data.status; // Return task status: 'SUCCESS', 'FAILURE', or 'PENDING'
    } catch (error) {
      console.error('Error polling task status:', error);
      return 'FAILURE'; 
    }
  };


/**
 * Downloads a report (individual or cohort) based on the task ID.
 * Handles fetching the pre-signed URL, downloading the file, and triggering the browser download.
 *
 * @param taskId - The ID of the task for the report generation.
 * @param reportType - The type of the report, 'individual' or 'cohort'.
 */
const downloadReport = async (taskId: string, reportType: 'individual' | 'cohort', onSuccessMessage: string, onFailureMessage: string) => {
  try {
    // Get the pre-signed URL
    const response = await axios.get(`/api/v1/study/download_report/`, {
      params: { id: taskId },
    });

    if (response.status !== 200 || !response.data.url) {
      throw new Error('Failed to retrieve the download URL.');
    }

    const fileUrl = response.data.url;

    // Download the file using the pre-signed URL
    const fileResponse = await axiosFileDownload.get(fileUrl, { responseType: 'arraybuffer' });

    // Create a downloadable link and trigger the download
    const blob = new Blob([fileResponse.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Set the filename dynamically based on the report type
    const fileName = `${studyTitle}_id_${studyId}_${reportType}_reports.zip`;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success(onSuccessMessage);
  } catch (error: any) {
    // Handle errors
    const errorMessage = error?.response?.data || `Error creating ${reportType} reports`;
    toast.error(onFailureMessage);
  }
};


  /* End of Export studies funtions */

  /* Delete responses */

  const deleteParticipantResponse = () => {
    return axios.post(`/api/v1/participants/${participantId}/modify_response/`, {}).then(()=>{
      revalidator.revalidate();
      fetchParticipants();
      toast.success("Participant Response Deleted!");
    }).catch((e)=>{
      toast.error(`Error deleting participant responses - ${e?.response.data}`)
    }).finally(()=>{
      setIsModifyResponsesModalOpen(false)
    })
  }

  /* Export Participant CSV */

  const exportParticipantCSV = () => {
    return axios.post(`/api/v1/study/${studyId}/Download_single_study_participant_CSV/`, undefined, { responseType: 'blob' })
    .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${studyTitle}_id_${studyId}_participants.csv`); // or any other extension
        document.body.appendChild(link);
        link.click();
    })
    .catch((e)=>{
      toast.error(`Error generating csv file - ${e?.response.data}`)
    }).finally(()=>{
      setIsExportParticipantCSVOpen(false)
    })
  }

  /* Export Study Responses CSV */

  const exportStudyResponsesCSV = (options: any) => {
    return axios.post(`/api/v1/study/${studyId}/export_study_responses_csv/`, options, { responseType: 'blob' })
    .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${studyTitle}_id_${studyId}_responses.csv`); // or any other extension
        document.body.appendChild(link);
        link.click();
    })
    .catch((e) => {
      if (e.response && e.response.data instanceof Blob) {
          // Convert the error blob back to text to read the error message
          const reader = new FileReader();
          reader.onload = function() {
              if (typeof reader.result === "string") {
                  // Convert the blob to JSON, then extract the "detail" field for the error message
                  const errorData = JSON.parse(reader.result);
                  toast.error(`Error generating csv file - ${errorData.detail}`);
              } else {
                  toast.error(`Error generating csv file - unexpected error`);
              }
          };
          reader.readAsText(e.response.data);
      } else {
          // This scenario shouldn't typically happen given the Axios config, but it's a safeguard
          toast.error(`Error generating csv file - ${e.message}`);
      }
    }).finally(()=>{
      setIsExportStudyResponsesCSVOpen(false)
    })
  }

  /* Export Study Responses SPSS */

  const exportStudyResponsesSPSS = (status: string) => {
    console.log(status)
    return axios.post(`/api/v1/study/${studyId}/export_SPSS_data/`, {status: status}, { responseType: 'blob' })
    .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${studyTitle}_id_${studyId}_responses.sav`); // or any other extension
        document.body.appendChild(link);
        link.click();
    })
    .catch((e)=>{
      toast.error(`Error generating csv file - ${e?.response.data}`)
    }).finally(()=>{
      setIsExportStudyResponsesSPSSOpen(false)
    })
  }

  const emailReportToAllParicipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/email_individual_report_all/`)
      revalidator.revalidate();
      fetchParticipants();
      fetchReportStatus();
      toast.success("All individual reports have been sent to participants.");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsEmailReportAllOpen(false)
    }
  }

  const emailReportToSelectedParticipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/email_individual_report/`, { ids: Array.from(selectedRow) })
      revalidator.revalidate();
      fetchParticipants();
      fetchReportStatus();
      toast.success("Task is successfully created: Emailing individual report to selected participants");
    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsEmailReportSelectiveOpen(false)
    }
  }

  const forceCalculationsToSelectedParticipants = async () => {
    try{
      await axios.post(`/api/v1/study/${studyId}/make_response_task/`, { ids: Array.from(selectedRow) })
      revalidator.revalidate();
      fetchParticipants();
      toast.success("Calculation Request received");

    }catch(e){
      toast.error(`${e}`)
    }finally{
      setIsForceCalculationsOpen(false)
      // Check if calculations have come through
      await wait(3000);
      revalidator.revalidate();
      fetchParticipants();
    }
  }


  /** Processing Modal */
  // const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);

  // const handleActivateParticipant = () => {
  //   //TO DO: update the POST API URL
  //   // axios
  //   //   .post(`/api/v1/study/${studyId}/activate_participant/`)
  //   //   .then((response) => {
  //   //     console.log("Activate participant response: ", response);
  //   //     setIsProcessingModalOpen(false);
  //   //     fetchParticipants();
  //   //   })
  //   //   .catch((error) => {
  //   //     console.log("Activate participant error: ", error);
  //   //     setIsProcessingModalOpen(false);
  //   //   });

  //   // Simulating API call with a 3-second delay
  //   setTimeout(() => {
  //     setIsProcessingModalOpen(false);
  //       toast.success(
  //       <div>
  //       Participants are activitated successfully!
  //       </div>,
  //       { autoClose: 30000, }
  //     ); 
      
  //   }, 3000)
  // }

  return (
    <>
     <Navbar className="mt-0 mb-2 font-sans" color="dark">
        <Navbar.Start>
          <span className="text-lg font-bold ml-2 mr-4">Study Participants</span>
        </Navbar.Start>
        <Navbar.End>
        <Select
            className="select mr-4"
            value={orderBy}
            onChange={(e) => changeOrderBy(e.target.value)}
          >
            <option disabled selected value="">
              Order by
            </option>
            <option value="id">ID - ASC</option>
            <option value="-id">ID - DESC</option>
          </Select>
          <Dropdown end={true}>
            <Dropdown.Toggle color="secondary"  button={true} className="w-72" id="StudyActionsDropdownButton">Study Actions</Dropdown.Toggle>
            <Dropdown.Menu className="w-72 z-10">
              <Link className="btn btn-ghost" id="StudyActionsDropdownAddParticipantsButton" to={`participants/upload`}>Add Participants</Link>
              <Button id="StudyActionsDropdownExportAllIndivialReportButton" color="ghost" disabled={!study?.active} onClick={()=>setIsExportAllModalOpen(true)}>Export All Individual Reports</Button>
              <Button id="StudyActionsDropdownExportCohortReportButton" color="ghost" disabled={!study?.active} onClick={openExportCohortModal}>Export Cohort Report</Button>
              <Button id="StudyActionsDropdownActivalAllParticipantsButton" color="ghost" disabled={!study?.active} onClick={openActivateParticipantsModal}>Activate All Participants</Button>
              <Button id="StudyActionsDropdownEmailStudyAllParticipantsButton" color="ghost" disabled={!study?.active} onClick={openEmailModal}>Email Study to All Participants</Button>
              <Button id="StudyActionsDropdownExportStudyParticipantCSVButton" color="ghost" onClick={() => setIsExportParticipantCSVOpen(true)}>Export Participants CSV</Button>
              <Button id="StudyActionsDropdownExportStudyResponsesCSVButton" color="ghost" onClick={() => setIsExportStudyResponsesCSVOpen(true)}>Export Study Responses CSV</Button>
              <Button id="StudyActionsDropdownExportStudyResponsesSPSSButton" color="ghost" onClick={() => setIsExportStudyResponsesSPSSOpen(true)}>Export Study Responses SPSS</Button>
              {/* <Button id="StudyActionsDropdownEmailReportAllParticipantsButton" color="ghost" onClick={() => setIsEmailReportAllOpen(true)}>Email individual report to all participants</Button> */}
            </Dropdown.Menu>
          </Dropdown>
        </Navbar.End>
      </Navbar>

      {participants?.total ? (
        <>
          <ParticipantTable
            participants={participants.results}
            navigateToParticipant={(id) => navigate(`participants/${id}`)}
            filter={filter}
            selectedRow={selectedRow}
            toggleSelection={toggleSelection}
            selectAllRow={selectAllRow}
            isAllRowSelected={isAllRowSelected}
            openModifyResponsesModal={openModifyResponsesModal}
            getParticipantId={getParticipantId}
            openViewReportModal={openViewReportModal}
            sending_report={emailedReportStatus}
          />

          <div className="flex justify-between">
            <div>
            <Dropdown vertical="top" className="" >
              <Dropdown.Toggle id="SelectedParticipantsDropdownButton" className="" >Selected Participants</Dropdown.Toggle>
              <Dropdown.Menu className="w-[450px] bg-base-200">
                <Button id="SelectedParticipantsDropdownActivateSelectedButton" name="activate_individual" color="ghost" disabled={selectedRow.size > 0 ? false : true } onClick={() => setIsActivateSelectedModalOpen(true)} className="btn-neutral">
                Activate Selected Participant's
                </Button>
                <Button id="SelectedParticipantsDropdownEmailStudySelectedButton" name="email_study_individual" color="ghost" disabled={selectedRow.size > 0 ? false : true } onClick={() => setIsEmailSelectedModalOpen(true)} className="btn-neutral">
                Email Study to Selected Participant's
                </Button>
                <Button 
                  id="StudyActionsDropdownEmailReportSelectiveParticipantsButton" 
                  color="ghost" 
                  disabled={selectedRow.size > 0 ? false : true } 
                  onClick={() => setIsEmailReportSelectiveOpen(true)}
                  style={{ display: 'none' }}  // HIDE email individual report feature
              >
                  Email individual report to selected participants
              </Button>
                <Button id="SelectedParticipantsDropdownExportReportSelectedButton" name="export_Individual" color="ghost" disabled={selectedRow.size > 0 ? false : true } onClick={() => setIsExportModalOpen(true)} className="btn-neutral">
                Export Selected Participant's individual report
                </Button>
                <Button id="SelectedParticipantsDropdownExportForceCalculationsButton" name="force_calculations" color="ghost" disabled={selectedRow.size > 0 ? false : true } onClick={() => setIsForceCalculationsOpen(true)} className="btn-neutral">
                Force Calculation of Study Equations
                </Button>
              </Dropdown.Menu>
            </Dropdown>
              <select
                id="ParticipantPageSizeInput"
                value={pageSize}
                onChange={(e) => changePageSize(e.target.value)}
                className="select select-bordered focus:outline-offset-0 ml-2"
              >
                <option id="ParticipantPageSizeInputOption1" value="10">10</option>
                <option id="ParticipantPageSizeInputOption2" value="20">20</option>
                <option id="ParticipantPageSizeInputOption3" value="50">50</option>
                <option id="ParticipantPageSizeInputOption4" value="100">100</option>
              </select>
            </div>
            <PaginationBar
              onChange={changePage}
              itemCount={participants.total}
              currentPage={participants.current_page}
              pageSize={participants.page_size}
            ></PaginationBar>
          </div>
        </>
      ) : (
        <p className="p-6">
          <i>No Participant Found</i>
        </p>
      )}
      {/* <ProcessingModal
        isOpen={isProcessingModalOpen}
        onClose={()=> setIsProcessingModalOpen(false)}
        handleConfirmProcess={handleActivateParticipant}
      /> */}
      <ActionConfirmationModal
        modalID="DeleteParticipantResponse"
        isOpen={isModifyResponsesModalOpen}
        onClose={()=>setIsModifyResponsesModalOpen(false)}
        onAction={deleteParticipantResponse}
        title={"Confirm deletion of participant's response"}
        content={deleteResponsesContent()}
      />
      <ActionConfirmationModal
        modalID="ExportModalContent"
        isOpen={isExportModalOpen}
        onClose={()=>setIsExportModalOpen(false)}
        onAction={exportSelectedRow}
        title={"Confirm export selected participant's individual report"}
        content={exportModalContent()}
      />
      <ActionConfirmationModal
        modalID="ExportAllRow"
        isOpen={isExportAllModalOpen}
        onClose={()=>setIsExportAllModalOpen(false)}
        onAction={() => {
          exportAllRow().then(taskId => {
            if (taskId) {
              startPolling(
                taskId,
                "individual",
                downloadReport,
                'Individual report generation complete! Downloading...',
                'Individual report generation failed. Check logs for more details.',
                () => setIsExportAllModalOpen(false) 
              );
              
            } else {
              toast.error('Failed to initiate individual report generation.');
              setIsExportAllModalOpen(false)
            }
          });
        }}
        title = "Export all completed individual reports"
        showInfoAlert
      />
      <CohortReportConfirmationModal
        isOpen={isCohortExportModalOpen}
        onClose={() => setIsCohortExportModalOpen(false)}
        onAction={(data) => {
          exportCohortExport(data).then(taskId => {
            if (taskId) {
              startPolling(
                taskId,
                "cohort",
                downloadReport, 
                'Cohort report generation complete! Downloading...',
                'Cohort report generation failed. Check logs for more details.',
                () => setIsCohortExportModalOpen(false)
              );
              
            } else {
              toast.error('Failed to initiate cohort report generation.');
            }
          });
        }}
        title="Confirm export cohort report"
      />
      <ActionConfirmationModal
        modalID="ActivateSelectedRow"
        isOpen={isActivateSelectedModalOpen}
        onClose={closeActivateSelectedModal}
        onAction={activateSelectedRow}
        title = "Activate selected participants"
      />
      <ActionConfirmationModal
        modalID="ActivateParticipants"
        isOpen={isActivateParticipantsModalOpen}
        onClose={closeActivateParticipantsModal}
        onAction={activateParticipants}
        title = "Activate All participants"
      />
      <ActionConfirmationModal
        modalID="EmailParticipants"
        isOpen={isEmailModalOpen}
        onClose={closeEmailModal}
        onAction={emailParticipants}
        title = "Email participants"
      />
      <ActionConfirmationModal
        modalID="EmailSelectedParticipants"
        isOpen={isEmailSelectedModalOpen}
        onClose={closeEmailSelectedModal}
        onAction={emailSelectedParticipants}
        title = "Email Selected participants"
      />
      <ActionConfirmationModal
        modalID="ExportParticipantCSV"
        isOpen={isExportParticipantCSVOpen}
        onClose={()=>setIsExportParticipantCSVOpen(false)}
        onAction={exportParticipantCSV}
        title = "Export Participant CSV"
      />
      <ExportResponsesCSVModal
        isOpen={isExportStudyResponsesCSVOpen}
        onClose={()=>setIsExportStudyResponsesCSVOpen(false)}
        onAction={exportStudyResponsesCSV}
        title = "Export Study Responses CSV"
      />
      <ActivateParticipantConfirmModal
        studyId={studyId}
        isOpen={isActivateModalOpen}
        closeModal={closeActivateModal}
        handleSuccessActivation={handleSuccessActivation}
      />
      <ExportResponsesSPSSModal
        isOpen={isExportStudyResponsesSPSSOpen}
        onClose={()=>setIsExportStudyResponsesSPSSOpen(false)}
        onAction={exportStudyResponsesSPSS}
        title = "Export Study Responses SPSS"
      />
      <ViewReportModal
        studyId={studyId}
        participantId={participantId}
        isOpen={isViewReportModalOpen}
        onClose={closeViewReportModal}
        downloadParticipantReport={downloadParticipantReport}
        onAction={() => console.log('Reports viewed?')}
      />
      <ActionConfirmationModal
        modalID="emailReportToAllParicipants"
        isOpen={isEmailReportAllOpen}
        onClose={()=>setIsEmailReportAllOpen(false)}
        onAction={emailReportToAllParicipants}
        title = "Email Individual report to all participants"
      />
      <ActionConfirmationModal
        modalID="emailReportToSelectedParticipants"
        isOpen={isEmailReportSelectiveOpen}
        onClose={()=>setIsEmailReportSelectiveOpen(false)}
        onAction={emailReportToSelectedParticipants}
        title = "Email Individual report to selected participants"
      />
      <ActionConfirmationModal
        modalID="forceCalculateEquationsToSelectedParticipants"
        isOpen={isForceCalculationsOpen}
        onClose={()=>setIsForceCalculationsOpen(false)}
        onAction={forceCalculationsToSelectedParticipants}
        title = "Force calculate equations"
      />
    </>
  );
};

export default ViewParticipants;
